import {
  DynamicTreeDataSourceOptions,
  NodeConfiguration,
  NodeData,
  NodeDataUtils,
  NodeOptions,
  TreeDataSourceOptions,
} from '../dataSourceTypes';
import { DraggableTreeDataSource } from './base/draggableDataSource';

export class DynamicTreeDataSource extends DraggableTreeDataSource<DynamicTreeDataSourceOptions> {
  constructor(options: DynamicTreeDataSourceOptions) {
    super(options);
    this.getInitialNodes(options);
  }

  /**
   * Method used to fetch node data Async when a node has been clicked.
   */
  public requestNodeData(data: NodeConfiguration): Promise<NodeOptions | null> {
    return this.options.requestNodeData(data);
  }

  /**
   * Method used to fetch root node data Async.
   */
  public requestRootNodeData(nodeId: string): Promise<NodeOptions | null> {
    return this.options.requestRootNodeData(nodeId);
  }

  /**
   * Method that opens up the node and fetches it child nodes.
   */
  public async toggleNodeOpen(node: NodeData, isOpen: boolean = !node.isOpen, useCacheOption = true): Promise<void> {
    node.isOpen = isOpen;
    const shouldFetchNodes =
      !node.leaf && node.isOpen && (!node.childNodeIds?.length || (useCacheOption && !this.options.cacheEnabled));

    node.isLoading = shouldFetchNodes;
    this.nodes.set(node.nodeId, node);

    if (shouldFetchNodes) {
      this.emitNodeChanges();

      // If pagination is enabled, load the first page of nodes
      if (this.options.hasPagination) {
        await this.loadPagedNodes(node);
      } else {
        // Original behavior for non-paginated loading
        await this.fetchAndSetNodes(() => this.requestNodeData(NodeDataUtils.convertToNodeConfig(node)));
        const updatedNode = this.nodes.get(node.nodeId);
        if (updatedNode) {
          this.nodes.set(updatedNode.nodeId, { ...updatedNode, isLoading: false });
        }
      }
    }

    this.emitNodeChanges();
  }

  private async getInitialNodes(options: TreeDataSourceOptions): Promise<void> {
    await Promise.all(options.rootNodeIds.map(nodeId => this.fetchAndSetNodes(() => this.requestRootNodeData(nodeId))));
    this.emitNodeChanges();
  }

  private async fetchAndSetNodes(fetchHandler: () => Promise<NodeOptions | null>): Promise<void> {
    const response = await fetchHandler();

    if (response) {
      const { currentNode, childNodes } = response;
      this.setNodes(currentNode, childNodes);
    }
  }

  public async loadPagedNodes(targetNode: NodeData, page?: number): Promise<void> {
    targetNode.isNextPageLoading = true;
    this.nodes.set(targetNode.nodeId, targetNode);
    this.emitNodeChanges();
    try {
      // Use the provided page or get it from the node
      const pageToLoad = page || targetNode.loadedPage || 1;
      // Construct node configuration with page parameter
      const nodeConfig = {
        ...NodeDataUtils.convertToNodeConfig(targetNode),
        page: pageToLoad,
      };

      const response = await this.options.requestNodeData(nodeConfig);

      if (response && response.childNodes && response.childNodes.length) {
        const newChildNodes: NodeData[] = response.childNodes;

        // Update the target node with the new child nodes
        targetNode.childNodeIds = targetNode.childNodeIds
          ? Array.from(new Set([...targetNode.childNodeIds, ...newChildNodes.map(node => node.nodeId)]))
          : newChildNodes.map(node => node.nodeId);

        targetNode.loadedPage = pageToLoad;
        targetNode.hasMoreChildNodes = response.hasMoreChildNodes || false;

        // Set parent and add nodes to the map
        newChildNodes.forEach((node: NodeData) => {
          node.parentNodeId = targetNode.nodeId;

          if (this.nodes.has(node.nodeId)) {
            return;
          }

          this.nodes.set(node.nodeId, node);
        });

        // Update the target node in the map
        this.nodes.set(targetNode.nodeId, targetNode);
        this.emitNodeChanges();
      } else {
        // No more items to load
        targetNode.hasMoreChildNodes = false;
        targetNode.isNextPageLoading = false;
        this.nodes.set(targetNode.nodeId, targetNode);
        this.emitNodeChanges();
      }
    } catch (error) {
      console.error('Error loading more nodes', error);
      targetNode.hasMoreChildNodes = false;
      targetNode.isNextPageLoading = false;
      this.nodes.set(targetNode.nodeId, targetNode);
      this.emitNodeChanges();
    } finally {
      targetNode.isLoading = false;
      targetNode.isNextPageLoading = false;
      this.nodes.set(targetNode.nodeId, targetNode);
      this.emitNodeChanges();
      this.loading$.next(false);
    }
  }
}
